import { objectHash } from "shared/utils/hash"

import category, { CategoryOperations } from "./editor/category"
import categoryOrProduct, { CategoryOrProductOperations } from "./editor/categoryOrProduct"
import charge, { ChargeOperations } from "./editor/charge"
import deal, { DealOperations } from "./editor/deal"
import dealLine, { DealLineOperations } from "./editor/dealLine"
import dealLineSku, { DealLineSkuOperations } from "./editor/dealLineSku"
import discount, { DiscountOperations } from "./editor/discount"
import option, { OptionOperations } from "./editor/option"
import optionList, { OptionListOperations } from "./editor/optionList"
import product, { ProductOperations } from "./editor/product"
import sku, { SkuOperations } from "./editor/sku"
import { CatalogAndHash, Change, OperationContext, Snapshot } from "./editor/types"
import variant, { VariantOperations } from "./editor/variant"
import { ICatalog } from "./models"
import { renumberCatalog } from "./utils/Catalog"

export class Editor {
  private current: CatalogAndHash
  private server: CatalogAndHash
  private readonly past: Snapshot[]
  private readonly future: Snapshot[]
  private readonly onChange: () => void
  private readonly currentUrl: () => string

  constructor(serverCatalog: ICatalog, onChange: () => void, currentUrl: () => string) {
    const hash = Editor.hash(serverCatalog, { isServer: true })
    this.current = { catalog: serverCatalog, hash }
    this.server = { catalog: serverCatalog, hash }
    this.past = []
    this.future = []
    this.onChange = onChange
    this.currentUrl = currentUrl
  }

  get category(): CategoryOperations {
    return category(this.operationContext())
  }

  get categoryOrProduct(): CategoryOrProductOperations {
    return categoryOrProduct(this.category, this.product)
  }

  get charge(): ChargeOperations {
    return charge(this.operationContext())
  }

  get deal(): DealOperations {
    return deal(this.operationContext())
  }

  get dealLine(): DealLineOperations {
    return dealLine(this.operationContext())
  }

  get dealLineSku(): DealLineSkuOperations {
    return dealLineSku(this.operationContext())
  }

  get discount(): DiscountOperations {
    return discount(this.operationContext())
  }

  get option(): OptionOperations {
    return option(this.operationContext())
  }

  get optionList(): OptionListOperations {
    return optionList(this.operationContext())
  }

  get product(): ProductOperations {
    return product(this.operationContext())
  }

  get sku(): SkuOperations {
    return sku(this.operationContext())
  }

  get variant(): VariantOperations {
    return variant(this.operationContext())
  }

  catalogSaved(serverCatalog: ICatalog): void {
    this.server = {
      catalog: serverCatalog,
      hash: Editor.hash(serverCatalog, { isServer: true }),
    }
  }

  rename(name: string): void {
    const catalog = { ...this.catalog, name }
    this.current = { catalog, hash: Editor.hash(catalog, { isServer: false }) }
    const serverCatalog = { ...this.server.catalog, name }
    this.server = { catalog: serverCatalog, hash: Editor.hash(serverCatalog, { isServer: true }) }
  }

  get catalog(): ICatalog {
    return this.current.catalog
  }

  get catalogHash(): string {
    return this.current.hash
  }

  get catalogServerHash(): string {
    return this.server.hash
  }

  // EditorStoreContext re-renders each time this hash changes.
  get hash(): string {
    return objectHash([this.current.hash, this.server.hash])
  }

  get unsaved(): boolean {
    return this.current.hash !== this.server.hash
  }

  undo(): string | undefined {
    return this.stepBack(this.past, this.future)
  }

  get canUndo(): boolean {
    return this.past.length > 0
  }

  redo(): string | undefined {
    return this.stepBack(this.future, this.past)
  }

  get canRedo(): boolean {
    return this.future.length > 0
  }

  private pushState(catalog: ICatalog, change: Change, collapseFn?: (previousChange: Change) => boolean): void {
    const hash = Editor.hash(catalog, { isServer: false })
    if (hash === this.current.hash) return

    if (this.previousSnapshot && collapseFn && collapseFn(this.previousSnapshot.change)) {
      // Collapse the current update with the previous one
      if (this.previousSnapshot.hash === hash) {
        this.past.pop() // The current update reverses the previous one
      } else {
        this.past[this.past.length - 1].change = change
      }
    } else {
      // Register a new update
      this.past.push(this.createSnapshot(change))
    }

    this.future.splice(0, this.future.length) // Empty the array
    this.current = { catalog, hash }
    this.notifyChange()
  }

  private get previousSnapshot(): Snapshot | undefined {
    return this.past.slice(-1)[0]
  }

  private stepBack(backward: Snapshot[], forward: Snapshot[]): string | undefined {
    const length = backward.length
    if (length === 0) return

    const lastUrl = backward[length - 1].url
    if (lastUrl !== this.currentUrl()) return lastUrl

    const { catalog, hash, change } = backward.pop()!
    forward.push(this.createSnapshot(change))
    this.current = { catalog, hash }
    this.notifyChange()
  }

  private notifyChange(): void {
    this.onChange()
  }

  private createSnapshot(change: Change): Snapshot {
    return { ...this.current, url: this.currentUrl(), change }
  }

  private operationContext(): OperationContext {
    return {
      catalog: this.catalog,
      pushState: (...args) => this.pushState(...args),
    }
  }

  private static hash(catalog: ICatalog, options: { isServer: boolean }): string {
    // Unlike server catalogs, client catalogs can have non-sequential ids (e.g. when a category is deleted).
    // We need to renumber the client catalog to ensure that hashes from the client and the server can be compared.
    if (!options.isServer) {
      catalog = renumberCatalog(catalog)
    }
    return objectHash(catalog)
  }
}
